import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// useLayoutEffect warns when a page is rendered to a string (the tests do);
// nothing is placed there anyway.
const useIsoLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

const HIDDEN = { position: 'fixed', top: 0, left: 0, visibility: 'hidden' };
const MARGIN = 8;

// Where a popover of `size` goes next to the `anchor` rect in a `viewport`:
// below, or above when only that fits. One taller than both sides is capped
// at the viewport height (it scrolls inside) and pushed up into view, so its
// footer can always be reached.
export function placePopover(anchor, size, viewport, align, offset) {
  const maxHeight = Math.max(0, viewport.height - 2 * MARGIN);
  const height = Math.min(size.height, maxHeight);
  let left = align === 'end' ? anchor.right - size.width : anchor.left;
  left = Math.max(MARGIN, Math.min(left, viewport.width - size.width - MARGIN));
  let top = anchor.bottom + offset;
  const above = anchor.top - offset - height;
  if (top + height > viewport.height - MARGIN) {
    if (above >= MARGIN) top = above;
    else top = Math.max(MARGIN, viewport.height - MARGIN - height);
  }
  return { top: Math.round(top), left: Math.round(left), maxHeight };
}

const TABBABLE = 'button, input, select, textarea, a[href], [tabindex]';

// Whether this keydown is a Tab off either end of the `popover`. It sits at
// the end of <body>, so the browser would carry on from there: the caller
// closes it and focuses its own control instead, without preventDefault, so
// the same Tab moves on from that control.
export function tabsOutOf(event, popover) {
  if (event.key !== 'Tab' || !popover) return false;
  const items = [...popover.querySelectorAll(TABBABLE)].filter(
    (el) => el.tabIndex >= 0 && !el.disabled,
  );
  const edge = event.shiftKey ? items[0] : items[items.length - 1];
  return !edge || event.target === edge;
}

// Placement and dismissal for a popover rendered through <Portal> (fixed to
// the viewport, so a glass panel's backdrop-filter or a scrolling dialog can
// neither re-anchor nor clip it). It opens below the anchor, flips above when
// there is no room below, stays inside the viewport, and follows the anchor
// on scroll and resize. A mousedown outside both elements or Escape calls
// `onClose(reason)`; Escape is caught in the capture phase, so it closes the
// popover without also closing the dialog the control sits in.
export function useAnchoredPopover({
  open,
  onClose,
  anchorRef,
  popoverRef,
  align = 'start',
  offset = 8,
  matchWidth = false,
}) {
  const [style, setStyle] = useState(HIDDEN);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useIsoLayoutEffect(() => {
    if (!open) {
      setStyle(HIDDEN);
      return undefined;
    }
    function place(event) {
      // The popover's own list scrolling must not re-place it.
      if (
        event?.type === 'scroll' &&
        popoverRef.current?.contains(event.target)
      )
        return;
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor || !popover) return;
      const a = anchor.getBoundingClientRect();
      const p = popover.getBoundingClientRect();
      const width = matchWidth ? Math.max(p.width, a.width) : p.width;
      const { top, left, maxHeight } = placePopover(
        a,
        { width, height: p.height },
        { width: window.innerWidth, height: window.innerHeight },
        align,
        offset,
      );
      setStyle({
        position: 'fixed',
        top,
        left,
        maxHeight,
        overflowY: 'auto',
        ...(matchWidth ? { minWidth: Math.round(a.width) } : null),
      });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const observer =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => place())
        : null;
    if (observer && popoverRef.current) observer.observe(popoverRef.current);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      observer?.disconnect();
    };
  }, [open, align, offset, matchWidth, anchorRef, popoverRef]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (anchorRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      onCloseRef.current?.('outside');
    }
    function onKeyDown(event) {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      onCloseRef.current?.('escape');
    }
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, anchorRef, popoverRef]);

  return style;
}
